<script setup lang="ts">
import { AdviceType } from '@ui/styles'
import Advice from '../advice/Advice.vue'
import CircleLoader from '../loader/CircleLoader.vue'

const props = withDefaults(
  defineProps<{
    source: string
    errorText?: string
    openLabel?: string
    native?: boolean
  }>(),
  {
    errorText: '',
    openLabel: '',
    native: undefined,
  },
)

const loadingFailed = ref(false)

const useNativeViewer = computed(() => {
  return props.native ?? (import.meta.client && shouldUseNativePdfViewer())
})

const VuePdfEmbed = defineAsyncComponent({
  loader: async () => {
    const [component] = await Promise.all([
      import('vue-pdf-embed'),
      import('vue-pdf-embed/dist/styles/annotationLayer.css'),
      import('vue-pdf-embed/dist/styles/textLayer.css'),
    ])

    return component.default
  },
  onError(error, _retry, fail) {
    loadingFailed.value = true
    console.error(error)

    fail()
  },
})

function shouldUseNativePdfViewer() {
  const userAgent = navigator.userAgent
  const isIos = /ipad|iphone|ipod/i.test(userAgent)
  const isSafari = /safari/i.test(userAgent) && !/chrome|chromium|android/i.test(userAgent)

  return isIos || isSafari
}

watch(
  () => props.source,
  () => {
    loadingFailed.value = false
  },
)
</script>

<template lang="pug">
.pdf-viewer.flex.col-12.h-100p
  Advice.pdf-error(v-if="loadingFailed" :type="AdviceType.Error" :text="errorText")
    template(#actions)
      a.pdf-link.typ-13-17-400(:href="source" target="_blank" rel="noopener noreferrer") {{ openLabel ? $t(openLabel) : source }}
  .pdf-frame(v-else)
    ClientOnly
      iframe.pdf-native.br-12.w-100p(v-if="useNativeViewer" :src="source" title="PDF" loading="lazy")
      Suspense(v-else)
        component.pdf-document(
          :is="VuePdfEmbed"
          :key="source"
          annotationLayer
          textLayer
          :source="source"
          @loadingFailed="loadingFailed = true"
        )
        template(#fallback)
          CircleLoader.pdf-loading.m-auto
      template(#fallback)
        CircleLoader.pdf-loading.m-auto
</template>

<style scoped>
.pdf-link {
  min-width: 0;
  color: var(--cue-content-primary-default);
}

.pdf-frame {
  min-height: 70vh;
  max-height: 70vh;

  @supports (min-height: 70dvh) {
    min-height: 70dvh;
    max-height: 70dvh;
  }
}

.pdf-native {
  height: 70vh;
  border: 0;

  @supports (height: 70dvh) {
    height: 70dvh;
  }
}

.pdf-loading,
.pdf-error {
  --uno: min-h-160 h-100p;
  color: var(--cue-content-neutral-secondary);
}
</style>
